import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { css, keyframes } from 'styled-components';
import { rgba } from 'polished';

import { Button } from './Button';
import { Portal } from './Portal';
import { GeometryObserver } from './Popover';
import { useDebounce } from 'usehooks-ts';

const fade = keyframes`
    0% { opacity: 0; };
    100% { opacity: 1; };
`;

const fadeOut = keyframes`
    0% { opacity: 1; };
    100% { opacity: 0; };
`;

const slide = keyframes`
    0% { transform: translateY(-100%); opacity: 0; }
    100% { transform: none; opacity: 1; }
`;

const slideOut = keyframes`
    0% { transform: none; opacity: 1; }
    100% { transform: translateY(-100%); opacity: 0; }
`;

const Phantom = styled(Box)<{ visible?: boolean }>`
	opacity: ${(props) => (props.visible ? 1 : 0)};
	transition: 0.9s
		${(props) => (props.visible ? `cubic-bezier(1, 0, 0, 1)` : `cubic-bezier(0, 1, 0, 1)`)} opacity;
`;

const Area = styled(Box)<{ left: number; top: number; width?: number; shift: number }>`
	position: fixed;
	top: ${(props) => props.top}px;
	left: ${(props) => props.left}px;
	${(props) => props.width !== undefined && `width: ${props.width}px;`}
	transform: translateY(${(props) => props.shift}px);
	transition: 1s cubic-bezier(0, 1, 0, 1) transform;
`;

const Contents = styled(Box)`
	box-shadow: 0px 8px 16px ${(props) => rgba(props.theme.colors.slateBlue, 0.25)};
	position: relative;
	z-index: 1;
	border-radius: 8px;
`;

const Toolbar = styled(Flex)<{ out?: boolean }>`
	animation: 1s cubic-bezier(0, 1, 0, 1) ${(props) => (props.out ? slideOut : slide)};
	animation-fill-mode: forwards;
`;

const Overlay = styled(Box)`
	background: ${(props) => rgba(props.theme.colors.slateBlue, 0.25)};
	position: fixed;
	top: 0px;
	left: 0px;
	width: 100%;
	height: 100%;
`;

const Fade = styled(Box)<{ out?: boolean }>`
	animation: 1s
		${({ out }) =>
			out ? css`cubic-bezier(1, 0, 0, 1) ${fadeOut}` : css`cubic-bezier(0, 1, 0, 1) ${fade}`};
	animation-fill-mode: forwards;
	z-index: 3;
	position: relative;
`;

export function WorkingArea(props: {
	flexible?: boolean;
	active?: boolean;
	children: (active: boolean) => ReactNode;
	onDismiss?: () => void;
	onCancel?: () => void;
	onSave?: () => Promise<void> | void;
}) {
	const { t } = useTranslation();

	const ref = useRef<HTMLDivElement>(null);
	const areaRef = useRef<HTMLDivElement>(null);

	const [loading, setLoading] = useState(false);
	const [immediate, setImmediate] = useState(props.active);

	const active = useDebounce(props.active, 750);

	useEffect(() => {
		setTimeout(() => setImmediate(props.active), 10);
	}, [props.active]);

	return (
		<>
			<Phantom
				ref={ref}
				flexGrow={props.flexible ? 0 : 1}
				flexShrink={props.flexible ? 0 : 1}
				visible={!immediate}
			>
				{props.children(false)}
			</Phantom>
			{(props.active || active) && (
				<Portal>
					<Fade out={!props.active}>
						<Overlay onClick={props.onDismiss} />
						<GeometryObserver innerRef={ref}>
							{({ top, left, width }) => {
								return (
									<Area
										ref={areaRef}
										top={top}
										shift={
											immediate
												? Math.min(
														top,
														window.innerHeight - (areaRef.current?.clientHeight ?? 0) - 50,
												  ) - top
												: 0
										}
										left={left}
										width={props.flexible ? undefined : width}
									>
										<Contents>{props.children(true)}</Contents>
										{(props.onCancel || props.onSave) && (
											<Toolbar mt={2} out={!props.active}>
												<Button
													type="secondary"
													small
													title={t('common.cancel')}
													mr={2}
													onClick={props.onCancel}
												/>
												<Button
													small
													title={t('common.save')}
													loading={loading}
													onClick={async () => {
														setLoading(true);
														await props.onSave?.();
														setLoading(false);
													}}
												/>
											</Toolbar>
										)}
									</Area>
								);
							}}
						</GeometryObserver>
					</Fade>
				</Portal>
			)}
		</>
	);
}
